import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Button, Icon, Input, Tooltip } from 'components/common';
import { DisabledButtonWrapper } from 'components/common/styled';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetSelectedLists,
  setModal,
  setOnlyEnriched,
  setSearch,
} from 'actions/lists';
import {
  getIncludePausedLists,
  getListsTableSearch,
  getOnlyEnriched,
  getSelectedLists,
  getShowMyListsOnly,
} from 'selectors/lists';
import { setShowMyListsOnly, setIncludePausedLists } from 'actions/lists';

import { colours } from 'utils/theme';
import {
  getAreAllSelected,
  getAreSomeSelected,
  getSelectedCount,
} from 'components/common/Table/MultiSelect/helpers';
import limits from './limits';
import { Switches, Switch } from '../Actions/styled';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import LinkButton from 'components/common/LinkButton';

const Container = styled.div`
  display: flex;
  align-items: center;
  > * {
    margin: 10px 0 0 0;
  }
  ${p => p.theme.breakpoint.tablet} {
    flex-wrap: wrap;
  }
  ${p => p.theme.breakpoint.mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
  .search-input {
    max-width: 400px;
    min-width: 250px;
  }
`;

const CreateNewListContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  > span {
    font-size: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

function Actions({ total, loading, view }) {
  const dispatch = useDispatch();
  const includePausedLists = useSelector(getIncludePausedLists);
  const showMyListsOnly = useSelector(getShowMyListsOnly);
  const onlyEnriched = useSelector(getOnlyEnriched);
  const search = useSelector(getListsTableSearch);
  const selectedLists = useSelector(getSelectedLists);
  const someSelected = useMemo(() => getAreSomeSelected(total, selectedLists), [
    selectedLists,
    total,
  ]);
  const allSelected = useMemo(() => getAreAllSelected(total, selectedLists), [
    selectedLists,
    total,
  ]);
  const selectedCount = useMemo(() => getSelectedCount(total, selectedLists), [
    selectedLists,
    total,
  ]);

  const handleSetModal = modal => {
    dispatch(setModal(modal));
  };

  const handleToggleMyLists = () => {
    if (!loading) {
      dispatch(setShowMyListsOnly(!showMyListsOnly));
    }
  };

  const handleToggleIncludePaused = () => {
    if (!loading) {
      dispatch(setIncludePausedLists(!includePausedLists));
    }
  };

  const handleToggleOnlyEnriched = () => {
    if (!loading) {
      dispatch(setOnlyEnriched(!onlyEnriched));
    }
  };

  const handleDeselectAll = () => {
    dispatch(resetSelectedLists());
  };

  const [
    localSearchValue,
    handleSearch,
    resetLocalSearchValue,
  ] = useDebouncedSearch(search, setSearch);

  useEffect(() => {
    resetLocalSearchValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const ButtonWrapper =
    selectedCount > limits.bulkDelete ? DisabledButtonWrapper : Fragment;
  return (
    <Container>
      <Input
        prefix={<Icon icon='keywords' color='rgba(0, 0, 0, 0.85)' size='1x' />}
        placeholder='Search for a List'
        className='search-input'
        onChange={handleSearch}
        value={localSearchValue}
        allowClear
      />
      {selectedCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ marginLeft: 10, fontSize: 13 }}>
            {selectedCount} Selected
          </div>
          <LinkButton
            aria-label='deselect all'
            onClick={handleDeselectAll}
            disabled={loading}
          >
            Deselect All
          </LinkButton>
        </div>
      )}
      <Switches>
        <Switch
          onChange={handleToggleMyLists}
          checked={showMyListsOnly}
          label='Only show my lists'
          disabled={loading}
        />
        <Switch
          onChange={handleToggleOnlyEnriched}
          checked={onlyEnriched}
          label='Only show enriched lists'
          disabled={loading}
        />
        <Switch
          onChange={handleToggleIncludePaused}
          checked={includePausedLists}
          label='Include paused lists'
          disabled={loading}
        />
      </Switches>

      <CreateNewListContainer>
        <ActionButtons>
          {(someSelected || allSelected) && (
            <>
              <Tooltip
                title={
                  selectedCount > limits.bulkDelete
                    ? `More than ${limits.bulkDelete} lists selected`
                    : ''
                }
                placement='right'
              >
                <ButtonWrapper>
                  <Button
                    size='small'
                    onClick={() => handleSetModal('bulkDelete')}
                    icon={
                      <Icon
                        icon='trash'
                        color={colours.black}
                        size='1x'
                        style={{ marginRight: '8px', color: 'currentColor' }}
                      />
                    }
                    disabled={loading || selectedCount > limits.bulkDelete}
                    style={{ marginLeft: 10 }}
                  >
                    {`Delete Selected List${selectedCount > 1 ? 's' : ''}`}
                  </Button>
                </ButtonWrapper>
              </Tooltip>
            </>
          )}
          <Button
            type='primary'
            size='small'
            onClick={() => handleSetModal('create')}
            disabled={loading}
          >
            Create New List
          </Button>
        </ActionButtons>
      </CreateNewListContainer>
    </Container>
  );
}

Actions.propTypes = {
  total: PropTypes.number,
  loading: PropTypes.bool,
};

export default Actions;
